/**
 * Standalone page for managing per-weekday schedules of clinic providers.
 * Includes a provider selector and a 7-column grid showing schedule blocks.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, ArrowBack, InfoOutlined } from '@mui/icons-material';
import { PageShell } from '../../components';
import { providerService } from '../../services/providerService';
import { professionalScheduleService } from '../../services/professionalScheduleService';
import { ProviderWeeklySchedule, ServiceType } from '../../types/provider';
import { WeekDay, WEEK_DAY_SHORT_NAMES, ClinicForSchedule } from '../../types/professionalSchedule';
import ProviderWeeklyScheduleModal from './components/ProviderWeeklyScheduleModal';
import ProviderDayScheduleModal from './components/ProviderDayScheduleModal';

const ORDERED_DAYS: WeekDay[] = [
  WeekDay.MONDAY,
  WeekDay.TUESDAY,
  WeekDay.WEDNESDAY,
  WeekDay.THURSDAY,
  WeekDay.FRIDAY,
  WeekDay.SATURDAY,
  WeekDay.SUNDAY,
];

const ProviderWeeklySchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clinics, setClinics] = useState<ClinicForSchedule[]>([]);
  const [selectedClinic, setSelectedClinic] = useState<ClinicForSchedule | null>(null);
  const [schedules, setSchedules] = useState<ProviderWeeklySchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDay, setEditingDay] = useState<WeekDay | null>(null);

  // Load clinics dropdown (with serviceType)
  useEffect(() => {
    const load = async () => {
      try {
        const list = await professionalScheduleService.getClinics();
        setClinics(list);
        const providerIdParam = searchParams.get('providerId');
        if (providerIdParam) {
          const clinic = list.find(c => c.id === Number(providerIdParam));
          if (clinic) setSelectedClinic(clinic);
        }
      } catch {
        setError('Erro ao carregar prestadores');
      } finally {
        setLoadingClinics(false);
      }
    };
    load();
  }, []);

  const loadSchedules = useCallback(async () => {
    if (!selectedClinic) return;
    setLoading(true);
    setError(null);
    try {
      const data = await providerService.getWeeklySchedule(selectedClinic.id);
      setSchedules(data);
    } catch {
      setError('Erro ao carregar horários');
    } finally {
      setLoading(false);
    }
  }, [selectedClinic]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const handleModalClose = () => {
    setModalOpen(false);
    // Reload after save
    loadSchedules();
  };

  const getBlocksForDay = (weekDay: number) =>
    schedules
      .filter(s => s.weekDay === weekDay)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

  const isHoraMarcada = selectedClinic?.serviceType === ServiceType.AGENDA_CLINICA;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Prestadores', path: '/providers' },
        { label: 'Horários Semanais' },
      ]}
      title="Horários por Dia da Semana"
      actions={
        <>
          <Tooltip
            title={'Esta tela permite configurar os horários de funcionamento de cada prestador por dia da semana. Esses horários definem os períodos (manhã e tarde) e a capacidade de atendimento simultâneo para clínicas do tipo "Hora Marcada". Selecione um prestador abaixo e utilize o botão de edição em cada dia ou o botão "Editar Horários" para configurar todos os dias de uma vez.'}
            arrow
            placement="bottom-start"
          >
            <InfoOutlined sx={{ fontSize: '1.1rem', color: 'text.secondary', cursor: 'help', alignSelf: 'center' }} />
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/providers')}
          >
            Voltar
          </Button>
          {selectedClinic && isHoraMarcada && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setModalOpen(true)}
            >
              Editar Horários
            </Button>
          )}
        </>
      }
    >
        {/* Provider selector */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <FormControl fullWidth>
              <InputLabel>Selecionar Prestador</InputLabel>
              <Select
                value={selectedClinic?.id ?? ''}
                label="Selecionar Prestador"
                onChange={e => {
                  const value = e.target.value;
                  const clinic = value ? clinics.find(c => c.id === Number(value)) : null;
                  setSelectedClinic(clinic ?? null);
                }}
                disabled={loadingClinics}
              >
                {clinics.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.tradeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {selectedClinic ? (
          !isHoraMarcada ? (
            /* Service type guidance alert */
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                O prestador <strong>{selectedClinic.tradeName}</strong> utiliza o tipo de atendimento <strong>{selectedClinic.serviceType || 'não definido'}</strong>.
              </Typography>
              <Typography variant="body2" gutterBottom>
                Os horários por dia da semana estão disponíveis apenas para prestadores com o tipo <strong>{ServiceType.AGENDA_CLINICA}</strong>.
              </Typography>
              {selectedClinic.serviceType === ServiceType.AGENDA_PROFISSIONAIS ? (
                <Typography variant="body2">
                  Para configurar os horários de atendimento deste prestador, acesse a tela de <strong>Escalas de Atendimento</strong> e cadastre as escalas dos profissionais vinculados.
                </Typography>
              ) : selectedClinic.serviceType === ServiceType.ORDEM_CHEGADA ? (
                <Typography variant="body2">
                  Para editar os horários de atendimento deste prestador, acesse o formulário de edição do prestador na aba <strong>Configuração de Atendimento</strong>.
                </Typography>
              ) : (
                <Typography variant="body2">
                  Para configurar, acesse o cadastro do prestador e defina o campo <strong>Tipo de Atendimento</strong>.
                </Typography>
              )}
            </Alert>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : schedules.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" gutterBottom>
                  Nenhum horário por dia configurado para este prestador.
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  O sistema usará os horários padrão do cadastro do prestador.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setModalOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Configurar Horários
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={1.5}>
              {ORDERED_DAYS.map(day => {
                const blocks = getBlocksForDay(day);
                const hasBlocks = blocks.length > 0;
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={day}>
                    <Card
                      sx={{
                        height: '100%',
                        opacity: hasBlocks ? 1 : 0.5,
                        border: hasBlocks ? '1px solid' : '1px dashed',
                        borderColor: hasBlocks ? 'primary.light' : 'divider',
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {WEEK_DAY_SHORT_NAMES[day]}
                          </Typography>
                          <IconButton size="small" onClick={() => setEditingDay(day)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {hasBlocks ? (
                          blocks.map((block, idx) => (
                            <Box key={block.id || idx} sx={{ mb: 0.5 }}>
                              <Chip
                                label={`${block.startTime} - ${block.endTime}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {block.capacity != null && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Cap: {block.capacity}
                                </Typography>
                              )}
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Fechado
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                Selecione um prestador para visualizar os horários semanais.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Modal geral (3 etapas) */}
        {selectedClinic && (
          <ProviderWeeklyScheduleModal
            open={modalOpen}
            onClose={handleModalClose}
            providerId={selectedClinic.id}
            providerName={selectedClinic.tradeName}
          />
        )}

        {/* Modal de edição por dia */}
        {selectedClinic && editingDay !== null && (
          <ProviderDayScheduleModal
            open
            onClose={() => setEditingDay(null)}
            onSaved={() => {
              setEditingDay(null);
              loadSchedules();
            }}
            providerId={selectedClinic.id}
            weekDay={editingDay}
          />
        )}
    </PageShell>
  );
};

export default ProviderWeeklySchedulePage;
